import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileKeyboardInset } from '../../hooks/useMobileKeyboardInset';
import { MOBILE_ISLAND_HOME_PATH, MOBILE_ISLAND_KEYBOARD_OPEN_INSET_PX } from './constants';
import MobileIslandDockActionsIsland from './MobileIslandDockActionsIsland';
import MobileIslandDockLayout from './MobileIslandDockLayout';
import MobileIslandDockLeadIsland from './MobileIslandDockLeadIsland';
import MobileIslandActionIsland from './MobileIslandActionIsland';
import MobileIslandSearchField from './MobileIslandSearchField';
import WorkbenchIconAction from '../common/WorkbenchIconAction';
import { useMobileIslandDockBodyClasses } from './useMobileIslandDockBodyClasses';
import { useMobileIslandSearchCollapseOnScroll } from './useMobileIslandSearchCollapseOnScroll';
import { kickWorkbenchMobileListSearchProgress } from '../common/workbenchMobileListProgress';
import { scrollWorkbenchMobileListToTop } from '../../utils/mobileScrollChrome';

/** 页面注册后的底部三岛（替换默认态） */
const MobileIslandDockContextual = ({ config }) => {
	const navigate = useNavigate();
	const [searchExpanded, setSearchExpanded] = useState(false);
	const [inputFocused, setInputFocused] = useState(false);
	const [searchDraft, setSearchDraft] = useState(() => config?.search?.appliedValue ?? '');
	const searchFocusedRef = useRef(false);
	const searchInputRef = useRef(null);
	const searchDraftRef = useRef('');
	const searchHandlersRef = useRef(config?.search);
	searchHandlersRef.current = config?.search;

	const hasSearch = Boolean(config?.search);
	const leadItems = config?.leadItems ?? [];
	const actions = config?.actions ?? [];

	const keyboardInsetPx = useMobileKeyboardInset(hasSearch && searchExpanded && inputFocused);
	const keyboardOpen = keyboardInsetPx >= MOBILE_ISLAND_KEYBOARD_OPEN_INSET_PX;

	useMobileIslandDockBodyClasses({
		contextual: true,
		searchExpanded: hasSearch && searchExpanded,
		searchFocused: keyboardOpen,
		resetKey: config?.id,
	});

	useEffect(() => {
		setSearchExpanded(false);
		setInputFocused(false);
		const applied = config?.search?.appliedValue ?? '';
		searchDraftRef.current = applied;
		setSearchDraft(applied);
		// 仅切换页面（config.id）时重置展开态；appliedValue 日常同步见下一 effect
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [config?.id]);

	useEffect(() => {
		if (searchExpanded || searchFocusedRef.current) {
			return;
		}
		const applied = config?.search?.appliedValue ?? '';
		searchDraftRef.current = applied;
		setSearchDraft(applied);
	}, [config?.search?.appliedValue, searchExpanded]);

	const scrollListToTop = useCallback(() => {
		scrollWorkbenchMobileListToTop({ behavior: 'auto' });
	}, []);

	const triggerSearch = useCallback(
		(value) => {
			const applied = searchHandlersRef.current?.appliedValue ?? '';
			if (String(value ?? '') === String(applied ?? '')) {
				return;
			}
			kickWorkbenchMobileListSearchProgress();
			searchHandlersRef.current?.onSubmit?.(value);
			scrollListToTop();
		},
		[scrollListToTop],
	);

	const handleCloseSearch = useCallback(() => {
		setSearchDraft('');
		searchHandlersRef.current?.onDismiss?.();
		setSearchExpanded(false);
		setInputFocused(false);
		searchFocusedRef.current = false;
	}, []);

	const handleCollapseSearch = useCallback(() => {
		const applied = searchHandlersRef.current?.appliedValue ?? '';
		searchDraftRef.current = applied;
		setSearchDraft(applied);
		searchInputRef.current?.blur();
		requestAnimationFrame(() => {
			setSearchExpanded(false);
			setInputFocused(false);
			searchFocusedRef.current = false;
		});
	}, []);

	useMobileIslandSearchCollapseOnScroll(hasSearch && searchExpanded, handleCollapseSearch, inputFocused);

	const handleOpenSearch = useCallback(() => {
		const applied = searchHandlersRef.current?.appliedValue ?? '';
		searchDraftRef.current = applied;
		setSearchDraft(applied);
		setSearchExpanded(true);
	}, []);

	const handleSearchFocusChange = useCallback((focused) => {
		searchFocusedRef.current = focused;
		setInputFocused(focused);
	}, []);

	const handleSearchChange = useCallback((value) => {
		searchDraftRef.current = value;
		setSearchDraft(value);
	}, []);

	const handleSearchSubmit = useCallback(
		(event) => {
			event?.preventDefault?.();
			triggerSearch(searchDraftRef.current);
			searchInputRef.current?.blur();
		},
		[triggerSearch],
	);

	const handleHomeClick = useCallback(() => {
		handleCloseSearch();
		navigate(MOBILE_ISLAND_HOME_PATH);
	}, [handleCloseSearch, navigate]);

	if (!leadItems.length) {
		return null;
	}

	const searchExpandedActive = hasSearch && searchExpanded;

	return (
		<MobileIslandDockLayout
			searchExpanded={searchExpandedActive}
			searchFocused={keyboardOpen}
			lead={<MobileIslandDockLeadIsland items={leadItems} onHomeClick={handleHomeClick} />}
			middle={actions.length ? <MobileIslandDockActionsIsland actions={actions} /> : null}
			search={
				hasSearch ? (
					searchExpanded ? (
						<MobileIslandActionIsland className='app-mobile-action-island--search-field' ariaLabel='搜索'>
							<MobileIslandSearchField
								inputRef={searchInputRef}
								inputId={config.search.inputId}
								name={config.search.name}
								value={searchDraft}
								onChange={handleSearchChange}
								onSubmit={handleSearchSubmit}
								onDismiss={handleCloseSearch}
								onFocusStateChange={handleSearchFocusChange}
								placeholder={config.search.placeholder}
							/>
						</MobileIslandActionIsland>
					) : (
						<MobileIslandActionIsland single ariaLabel='搜索'>
							<WorkbenchIconAction
								className='app-mobile-action-island__search'
								icon='search'
								onClick={handleOpenSearch}
								active={Boolean(config.search.appliedValue?.trim())}
								title='搜索'
								ariaLabel='搜索'
								ariaExpanded={searchExpanded}
							/>
						</MobileIslandActionIsland>
					)
				) : null
			}
		/>
	);
};

MobileIslandDockContextual.propTypes = {
	config: PropTypes.shape({
		id: PropTypes.string.isRequired,
		leadItems: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.string.isRequired,
				icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
				iconComponent: PropTypes.elementType,
				label: PropTypes.string.isRequired,
				onClick: PropTypes.func,
			}),
		),
		actions: PropTypes.array,
		search: PropTypes.shape({
			inputId: PropTypes.string.isRequired,
			name: PropTypes.string,
			appliedValue: PropTypes.string,
			onSubmit: PropTypes.func,
			onDismiss: PropTypes.func,
			placeholder: PropTypes.string,
		}),
	}).isRequired,
};

export default MobileIslandDockContextual;
